import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import {
  createRedemption as createRedemptionService,
  listRedemptions as listRedemptionsService,
  importRedemptionsCsv as importRedemptionsCsvService,
} from "../services/redemptionsService";

const initialOfferBucket = () => ({
  items: [],
  lastKey: null,
  hasMore: true,
  loading: false,
  error: null,
});

const initialState = {
  byOffer: {},

  creating: false,
  createError: null,

  importing: false,
  importError: null,
  importSummary: null,
};

const toErrorMessage = (err, fallback) => {
  if (!err) return fallback;
  if (typeof err === "string") return err;
  return err?.message || fallback;
};

const getRedemptionId = (r) =>
  r?.redemptionId || r?.id || r?._id || r?.orderId || null;

const dedupeById = (items, getId) => {
  const seen = new Set();
  const out = [];
  for (const item of items || []) {
    const id = getId(item) || JSON.stringify(item);
    if (seen.has(id)) continue;
    seen.add(id);
    out.push(item);
  }
  return out;
};

const parseListResponse = (resp) => {
  if (Array.isArray(resp)) return { items: resp, lastKey: null };
  if (Array.isArray(resp?.items)) {
    return {
      items: resp.items,
      lastKey: resp.last_key ?? resp.lastKey ?? null,
    };
  }
  return { items: [], lastKey: null };
};

/**
 * listRedemptionsAsync({ offerId, params?, append? })
 * params supports: from,to,q,creatorId,inviteId,limit,last_key
 */
export const listRedemptionsAsync = createAsyncThunk(
  "redemptions/listByOffer",
  async ({ offerId, params = {}, append = false }, { rejectWithValue }) => {
    try {
      if (!offerId) throw new Error("offerId is required to list redemptions.");
      const resp = await listRedemptionsService(offerId, params);
      const parsed = parseListResponse(resp);
      return { offerId, ...parsed, append };
    } catch (e) {
      return rejectWithValue(
        toErrorMessage(e, "Failed to load redemptions.")
      );
    }
  }
);

export const createRedemptionAsync = createAsyncThunk(
  "redemptions/create",
  async ({ offerId, payload }, { rejectWithValue }) => {
    try {
      if (!offerId)
        throw new Error("offerId is required to create redemption.");
      const created = await createRedemptionService(offerId, payload);
      return { offerId, created: created || null };
    } catch (e) {
      return rejectWithValue(
        toErrorMessage(e, "Failed to create redemption.")
      );
    }
  }
);

export const importRedemptionsCsvAsync = createAsyncThunk(
  "redemptions/importCsv",
  async ({ offerId, csvText, source = "manual" }, { rejectWithValue }) => {
    try {
      if (!offerId)
        throw new Error("offerId is required to import redemptions.");
      if (!csvText || typeof csvText !== "string") {
        throw new Error("CSV text is required to import redemptions.");
      }

      const result = await importRedemptionsCsvService(
        offerId,
        csvText,
        source
      );

      return { offerId, result: result || null };
    } catch (e) {
      return rejectWithValue(
        toErrorMessage(e, "Failed to import redemptions CSV.")
      );
    }
  }
);

const redemptionsSlice = createSlice({
  name: "redemptions",
  initialState,
  reducers: {
    clearRedemptionsErrors(state) {
      Object.values(state.byOffer).forEach((b) => (b.error = null));
      state.createError = null;
      state.importError = null;
    },
    resetRedemptionsState() {
      return { ...initialState };
    },
    resetOfferRedemptions(state, action) {
      const offerId = action.payload;
      if (!offerId) return;
      state.byOffer[offerId] = initialOfferBucket();
    },
    clearRedemptionsImportState(state) {
      state.importing = false;
      state.importError = null;
      state.importSummary = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // LIST
      .addCase(listRedemptionsAsync.pending, (state, action) => {
        const offerId = action.meta.arg?.offerId;
        if (!offerId) return;
        if (!state.byOffer[offerId]) {
          state.byOffer[offerId] = initialOfferBucket();
        }
        state.byOffer[offerId].loading = true;
        state.byOffer[offerId].error = null;
      })
      .addCase(listRedemptionsAsync.fulfilled, (state, action) => {
        const { offerId, items, lastKey, append } = action.payload || {};
        if (!offerId) return;
        if (!state.byOffer[offerId]) {
          state.byOffer[offerId] = initialOfferBucket();
        }

        const bucket = state.byOffer[offerId];
        bucket.loading = false;

        const merged = append
          ? [...bucket.items, ...(items || [])]
          : items || [];
        bucket.items = dedupeById(merged, getRedemptionId);

        bucket.lastKey = lastKey || null;
        bucket.hasMore = Boolean(lastKey);
      })
      .addCase(listRedemptionsAsync.rejected, (state, action) => {
        const offerId = action.meta.arg?.offerId;
        if (!offerId) return;
        if (!state.byOffer[offerId]) {
          state.byOffer[offerId] = initialOfferBucket();
        }
        state.byOffer[offerId].loading = false;
        state.byOffer[offerId].error =
          action.payload || "Failed to load redemptions.";
      })

      // CREATE
      .addCase(createRedemptionAsync.pending, (state) => {
        state.creating = true;
        state.createError = null;
      })
      .addCase(createRedemptionAsync.fulfilled, (state, action) => {
        state.creating = false;
        const offerId = action.payload?.offerId;
        const created = action.payload?.created;
        if (!offerId || !created) return;

        if (!state.byOffer[offerId]) {
          state.byOffer[offerId] = initialOfferBucket();
        }
        const bucket = state.byOffer[offerId];

        const id = getRedemptionId(created);
        if (id) {
          bucket.items = bucket.items.filter(
            (r) => getRedemptionId(r) !== id
          );
        }

        bucket.items = [created, ...bucket.items];
      })
      .addCase(createRedemptionAsync.rejected, (state, action) => {
        state.creating = false;
        state.createError =
          action.payload || "Failed to create redemption.";
      })

      // IMPORT CSV
      .addCase(importRedemptionsCsvAsync.pending, (state) => {
        state.importing = true;
        state.importError = null;
        state.importSummary = null;
      })
      .addCase(importRedemptionsCsvAsync.fulfilled, (state, action) => {
        state.importing = false;
        state.importSummary = action.payload?.result || null;
      })
      .addCase(importRedemptionsCsvAsync.rejected, (state, action) => {
        state.importing = false;
        state.importError =
          action.payload || "Failed to import redemptions CSV.";
      });
  },
});

export const {
  clearRedemptionsErrors,
  resetRedemptionsState,
  resetOfferRedemptions,
  clearRedemptionsImportState,
} = redemptionsSlice.actions;

export const selectRedemptionsBucket = (state, offerId) =>
  state.redemptions?.byOffer?.[offerId] || initialOfferBucket();

export const selectRedemptions = (state, offerId) =>
  selectRedemptionsBucket(state, offerId).items;
export const selectRedemptionsLoading = (state, offerId) =>
  selectRedemptionsBucket(state, offerId).loading;
export const selectRedemptionsError = (state, offerId) =>
  selectRedemptionsBucket(state, offerId).error;
export const selectRedemptionsLastKey = (state, offerId) =>
  selectRedemptionsBucket(state, offerId).lastKey;
export const selectRedemptionsHasMore = (state, offerId) =>
  selectRedemptionsBucket(state, offerId).hasMore;

export const selectRedemptionCreating = (state) =>
  state.redemptions?.creating || false;
export const selectRedemptionCreateError = (state) =>
  state.redemptions?.createError || null;

export const selectRedemptionsImporting = (state) =>
  state.redemptions?.importing || false;
export const selectRedemptionsImportError = (state) =>
  state.redemptions?.importError || null;
export const selectRedemptionsImportSummary = (state) =>
  state.redemptions?.importSummary || null;

export default redemptionsSlice.reducer;